import { FunctionComponent, useEffect, useRef } from 'react'
import { useFlag } from '@afterpaytouch/feature'
import { Features } from '../../model/features'
import { isCashGrantHandoffPending } from './cashAuthJourney'
import { buildJourneyStartedEvent, buildRecordCompletedJourneyPayload } from './contract'
import { EsidContextProps, useEsid } from './EsidContext'
import { clearJourneyMarker, readJourneyMarker, writeJourneyMarker } from './journeyMarker'
import { datadogUserJourneySink, UserJourneySink } from './sink'
import { JourneyOutcome, JourneyTerminationReason, UserJourney } from './types'
import { useUserJourneyContext } from './UserJourneyContext'

export interface UserJourneyReportingProps {
  sink?: UserJourneySink
}

/**
 * Emits journey events to the sink, gated by the user-journey-instrumentation flag:
 * a started event on every journey start, and a completed event (full
 * RecordCompletedJourney contract) on completion — including restarts, which complete
 * the prior instance as TERMINATED/JOURNEY_RESTARTED before the new one starts.
 * Listens synchronously via subscribe so completions are captured even when the page
 * navigates away right after.
 *
 * Restart reconciliation (CASHDAP-3407): a sessionStorage marker mirrors the in-flight
 * journey and is cleared whenever a completion is reported — including the pagehide
 * PROCESS_KILLED anchor, which dispatches synchronously before unload. A marker still
 * present on the next page load therefore means the previous page died without
 * reporting any terminal outcome (crash, or pagehide never fired), so it is reconciled
 * here as TERMINATED/JOURNEY_RESTARTED — the instrumentation-gap semantics the
 * validation guide gives that reason. Its ended_at/duration are reconcile-time, not
 * death-time — after a true crash the timeout timer never fires, so the skew is
 * unbounded until the tab reloads (accepted; treat JOURNEY_RESTARTED durations as
 * upper estimates). Single-slot marker: tracks the most recently started journey,
 * which is all this app has. A marker annotated `handoff` (Cash grant ceremony,
 * CASHDAP-3409) is skipped here — the cash-auth return anchor resumes it instead, and
 * resumed journeys keep reporting under their original page load's esid.
 *
 * Known gap (accepted): the flag is live — if LaunchDarkly flips it mid-session, a
 * journey in flight at the flip loses its completed event (disable) or reports a
 * completion with no matching started event (enable). Flag ramps are effectively
 * page-load-granularity, so this skews at most the sessions active during a ramp.
 * A mid-session disable also drops the marker: the completion anchors are not
 * flag-gated (only the START anchor is), so a journey in flight at the flip still
 * completes during the OFF window — but the only clearJourneyMarker() sites live in
 * this gated effect, so a held marker would survive its journey's real completion and
 * detonate on a later enabled load as a phantom TERMINATED/JOURNEY_RESTARTED for a
 * journey that already ended. A re-enable restores the marker for a journey still in
 * flight, so the drop costs marker coverage only while the flag is OFF — the accepted
 * loss shrinks to completions dispatched during the OFF window itself. A load that
 * STARTS disabled keeps its inherited marker
 * instead: that journey belongs to a previous load, nothing in this session can
 * complete it, and reconciling it when the flag returns is a true "died without
 * reporting".
 */
export const UserJourneyReporting: FunctionComponent<UserJourneyReportingProps> = ({ sink = datadogUserJourneySink }) => {
  const enabled = useFlag(Features.USER_JOURNEY_INSTRUMENTATION)
  const { active, subscribe } = useUserJourneyContext()
  const { esid }: EsidContextProps = useEsid()
  const reportedCompletions = useRef<Set<string>>(new Set())
  const reconciledMarker = useRef(false)
  // Journey id → the esid its started event was reported under on a previous page load.
  // A journey resumed across the Cash grant handoff must complete under that original
  // esid or the analytical started−terminal derivation double-counts both sessions.
  const resumedEsids = useRef<Map<string, string>>(new Map())
  // Mirrored into a ref so the re-enable restore below can read the in-flight journey
  // without journey-state churn re-running the effect
  const activeRef = useRef(active)
  activeRef.current = active

  useEffect(() => {
    if (!enabled) {
      // reconciledMarker distinguishes the two disabled cases (see doc comment): true
      // means an enabled run already happened this load, so the marker tracks a
      // journey the un-gated anchors can still silently complete — drop it before it
      // can become a phantom restart. False means the load started disabled and the
      // marker is a previous load's orphan — keep it reconcilable.
      if (reconciledMarker.current) {
        clearJourneyMarker()
      }
      return undefined
    }

    const reportCompleted = (journey: UserJourney, outcome: JourneyOutcome, terminationReason?: JourneyTerminationReason): void => {
      // Defensive: the reducer already guards against completing a journey twice, but a
      // journey instance (id is a per-instance uuid) must never be reported completed
      // twice whatever future dispatch paths deliver it here
      if (reportedCompletions.current.has(journey.id)) {
        return
      }
      reportedCompletions.current.add(journey.id)
      clearJourneyMarker()
      sink.journeyCompleted(buildRecordCompletedJourneyPayload(journey, outcome, terminationReason, resumedEsids.current.get(journey.id) ?? esid))
    }

    // Once per page load only: on an effect re-run (e.g. the flag flipping off and on
    // again) the marker belongs to the journey currently in flight, not an orphan.
    // Reported under the marker's own esid so the completion joins its started event's
    // session, and guarded so a reconciliation failure can't prevent the subscribe below.
    if (!reconciledMarker.current) {
      reconciledMarker.current = true
      try {
        // A handoff-annotated marker is not an orphan — the cash-auth return anchor
        // resumes it (cashAuthJourney.ts); if the customer instead restarted checkout
        // from the top, the new START simply overwrites it and the handed-off journey
        // is counted analytically.
        const orphaned = readJourneyMarker()
        if (orphaned !== undefined && orphaned.handoff !== true && !reportedCompletions.current.has(orphaned.journey.id)) {
          reportedCompletions.current.add(orphaned.journey.id)
          clearJourneyMarker()
          sink.journeyCompleted(
            buildRecordCompletedJourneyPayload(orphaned.journey, JourneyOutcome.TERMINATED, JourneyTerminationReason.JOURNEY_RESTARTED, orphaned.esid)
          )
        }
      } catch {
        // reconciliation is best-effort; live reporting must still start
      }
    } else {
      // A mid-session disable dropped the marker (the !enabled branch above); if the
      // journey is still in flight on re-enable, restore it so a crash before the next
      // journey action still reconciles as JOURNEY_RESTARTED on the next load. A
      // journey that silently completed during the OFF window leaves active undefined —
      // nothing is restored, so the phantom-restart guarantee holds. Restored under the
      // esid its started event was reported with (a resumed journey keeps its original
      // load's esid), and re-asserting any pending handoff annotation so the restore
      // can't turn a handed-off journey back into a reconcilable orphan.
      const activeJourney = activeRef.current
      if (activeJourney !== undefined) {
        writeJourneyMarker(activeJourney, resumedEsids.current.get(activeJourney.id) ?? esid, isCashGrantHandoffPending())
      }
    }

    return subscribe(({ action, prevState, state }) => {
      switch (action.type) {
        case 'USER_JOURNEY_START': {
          const prior = prevState.journeys[action.name]
          if (prior !== undefined) {
            reportCompleted(prior, JourneyOutcome.TERMINATED, JourneyTerminationReason.JOURNEY_RESTARTED)
          }
          const started = state.journeys[action.name]
          if (started !== undefined) {
            writeJourneyMarker(started, esid)
            sink.journeyStarted(buildJourneyStartedEvent(started, esid))
          }
          break
        }
        case 'USER_JOURNEY_RESUME': {
          // No started event — it was emitted when the journey originally started, on
          // the page load whose esid the action carries. Rewriting the marker drops the
          // handoff annotation so a later death reconciles normally.
          const resumedJourney = state.journeys[action.name]
          if (resumedJourney !== undefined && prevState.journeys[action.name] === undefined) {
            resumedEsids.current.set(resumedJourney.id, action.esid)
            writeJourneyMarker(resumedJourney, action.esid)
          }
          break
        }
        case 'USER_JOURNEY_COMPLETE': {
          const journey = prevState.journeys[action.name]
          if (journey !== undefined) {
            reportCompleted(journey, action.outcome, action.terminationReason)
          }
          break
        }
        default: {
          // Signals, tags and variant mutate the journey — keep the marker current so a
          // reconciled restart reports the journey as it last looked. Re-assert the
          // handoff annotation: a poll tick or network signal can land after
          // beginCashGrantHandoff() while the departing page is still alive, and a
          // plain write here would clobber the annotation and break the resume.
          const journey = state.journeys[action.name]
          if (journey !== undefined) {
            writeJourneyMarker(journey, resumedEsids.current.get(journey.id) ?? esid, isCashGrantHandoffPending())
          }
          break
        }
      }
    })
  }, [enabled, subscribe, esid, sink])

  return null
}
